/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UnknownAction, ThunkDispatch } from '@reduxjs/toolkit';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import type { Credentials } from '@opengeoweb/api';
import { getQueryClient } from '@opengeoweb/api';
import type { InitialMapProps, InitialProps } from '@opengeoweb/core';
import {
  createViewObjectForNewMap,
  genericActions,
  genericSelectors,
  mapActions,
  mapSelectors,
  routerActions,
  syncConstants,
  syncGroupsActions,
  syncGroupsSelectors,
} from '@opengeoweb/store';
import { timeSeriesActions } from '@opengeoweb/time-series';
import {
  getErrorMessage,
  getListInChunks,
  getLoadingWorkspace,
  getSyncGroups,
  getViewPresetState,
  toViewsArray,
} from './utils';
import { viewpresetQueryOptions } from '../../api/viewpreset-api/queryOptions';
import { getWorkspaceRouteUrl } from '../../utils/routes';
import type {
  AppStore,
  WorkspaceActionType,
  WorkspaceModuleStore,
} from '../types';
import {
  getViewIds,
  getViewPresetToSendToBackend,
  getViewType,
  getWorkspaceState,
} from './selectors';
import { viewPresetActions } from '../viewPresets';
import { workspaceActions } from './reducer';
import type {
  WorkspaceFetchWorkspacePayload,
  WorkspaceViewType,
} from './types';
import { WorkspaceErrorType, WorkspacePresetAction } from './types';
import { ERROR_NOT_FOUND } from '../../api/workspace-api/fakeApi/mockData';
import { workspaceQueryOptions } from '../../api/workspace-api/queryOptions';
import { emptyWorkspaceBEData } from '../workspaceList/listener';
import { workspaceListActions } from '../workspaceList/reducer';
import { createCustomEmptyMapWorkspace } from '../workspaceList/utils';

export const workspaceListener = createListenerMiddleware<AppStore>();

workspaceListener.startListening({
  actionCreator: mapActions.registerMap,
  effect: async ({ payload }, listenerApi) => {
    const { mapId } = payload;
    const viewType = getViewType(listenerApi.getState());

    if (viewType === 'tiledWindow') {
      const workspaceState = getWorkspaceState(listenerApi.getState());
      // if it can't find the id, it's a view with multiple maps
      const mapPresetId =
        workspaceState!.views!.byId![mapId]?.id ??
        workspaceState!.views!.byId![workspaceState.views.allIds[0]].id!;

      listenerApi.dispatch(
        viewPresetActions.registerViewPreset({
          panelId: mapId,
          viewPresetId: mapPresetId,
        }),
      );
    }
  },
});

workspaceListener.startListening({
  actionCreator: mapActions.unregisterMap,
  effect: async ({ payload }, listenerApi) => {
    const { mapId } = payload;
    const viewType = getViewType(listenerApi.getState());

    if (viewType === 'tiledWindow') {
      listenerApi.dispatch(
        viewPresetActions.unregisterViewPreset({ panelId: mapId }),
      );
    }
  },
});

workspaceListener.startListening({
  actionCreator: workspaceActions.changePreset,
  effect: async ({ payload }, listenerApi) => {
    // Cancel any in-progress instances of this listener
    listenerApi.cancelActiveListeners();
    // Unmount old components before changing workspace preset

    // Check state BEFORE reducer ran - if there was a workspace,
    // wait for React to unmount old components before setting up new ones
    const originalMosaicNode =
      listenerApi.getOriginalState().workspace?.mosaicNode;

    if (originalMosaicNode) {
      await listenerApi.take(workspaceActions.teardownComplete.match);
    }

    listenerApi.dispatch(timeSeriesActions.resetTimeSeriesPanels());
    listenerApi.dispatch(syncGroupsActions.syncGroupClear());
    listenerApi.dispatch(workspaceActions.setPreset(payload));
    const allSyncGroups = getSyncGroups(payload.workspacePreset.syncGroups);
    allSyncGroups.map((syncGroup) =>
      listenerApi.dispatch(syncGroupsActions.syncGroupAddGroup(syncGroup)),
    );

    listenerApi.dispatch(
      genericActions.setLinkedMap({
        newLinks: payload.workspacePreset.linking ?? {},
      }),
    );

    const isTimeScrollingEnabled =
      payload.workspacePreset.isTimeScrollingEnabled ?? false;
    listenerApi.dispatch(
      syncGroupsActions.syncGroupToggleIsTimeScrollingEnabled({
        isTimeScrollingEnabled,
      }),
    );

    const views = toViewsArray(payload.workspacePreset.views);

    await Promise.all(
      views.map(async (view) => {
        const syncGroupsIds = ((
          view.initialProps as InitialMapProps | undefined
        )?.syncGroupsIds ?? []) as string[];

        if (!syncGroupsIds.length || !view.mosaicNodeId) {
          return;
        }

        syncGroupsIds.forEach((groupId) =>
          listenerApi.dispatch(
            syncGroupsActions.syncGroupAddTarget({
              groupId,
              targetId: view.mosaicNodeId,
              linked: true,
            }),
          ),
        );
      }),
    );

    views.forEach((view) => {
      const panelId = view.mosaicNodeId;
      if (!panelId) {
        return;
      }

      const hasInlineConfig =
        !!view.componentType && view.initialProps !== undefined;

      listenerApi.dispatch(
        viewPresetActions.setViewPresetInline({
          panelId,
          isInline: hasInlineConfig,
        }),
      );
    });

    if (payload.markAsChanged) {
      listenerApi.dispatch(workspaceActions.markWorkspaceChanged());
      listenerApi.dispatch(
        routerActions.navigateToUrl({ url: getWorkspaceRouteUrl() }),
      );
    }
  },
});

export const fetchWorkspaceViewPreset = async (
  dispatch: ThunkDispatch<WorkspaceModuleStore, unknown, UnknownAction>,
  viewPresetId: string,
  mosaicNodeId: string,
  credentials?: Credentials,
): Promise<void> => {
  try {
    // set the view as loading
    dispatch(
      workspaceActions.updateWorkspaceView({
        mosaicNodeId,
        viewPreset: getViewPresetState(viewPresetId, mosaicNodeId, 'loading'),
      }),
    );

    // fetch view
    const data = await getQueryClient().fetchQuery(
      viewpresetQueryOptions.getViewPreset(viewPresetId, credentials),
    );

    dispatch(
      viewPresetActions.fetchedViewPreset({
        viewPreset: data,
        viewPresetId: data.id!,
        panelId: mosaicNodeId,
      }),
    );

    // sync all connected syncgroups of viewPreset to the store
    const syncGroupIds =
      (data.initialProps as InitialMapProps)?.syncGroupsIds || [];
    if (syncGroupIds?.length) {
      syncGroupIds.forEach((syncGroupId: string) =>
        dispatch(
          syncGroupsActions.syncGroupAddTarget({
            groupId: syncGroupId,
            targetId: mosaicNodeId,
            linked: true,
          }),
        ),
      );
    }

    // update view with fetched data
    if (data) {
      dispatch(
        workspaceActions.updateWorkspaceView({
          mosaicNodeId,
          viewPreset: data as WorkspaceViewType,
        }),
      );
    }
  } catch (_error) {
    dispatch(
      workspaceActions.updateWorkspaceView({
        mosaicNodeId,
        viewPreset: getViewPresetState(viewPresetId, mosaicNodeId, 'error'),
      }),
    );
  }
};

workspaceListener.startListening({
  actionCreator: workspaceActions.fetchWorkspaceViewPreset,
  effect: async ({ payload }, listenerApi) => {
    const { viewPresetId, mosaicNodeId } = payload;
    const { common } = listenerApi.getState();

    await fetchWorkspaceViewPreset(
      listenerApi.dispatch,
      viewPresetId,
      mosaicNodeId,
      common?.auth.credentials,
    );
  },
});

workspaceListener.startListening({
  matcher: isAnyOf(
    workspaceActions.fetchWorkspace,
    routerActions.navigateBackOrForward,
  ),
  effect: async (action, listenerApi) => {
    // Cancel any in-progress instances of this listener
    listenerApi.cancelActiveListeners();
    const { common } = listenerApi.getState();
    const payload = action.payload as WorkspaceFetchWorkspacePayload;
    const { workspaceId } = payload;

    try {
      if (action.type === workspaceActions.fetchWorkspace.type) {
        listenerApi.dispatch(
          routerActions.navigateToUrl({
            url: getWorkspaceRouteUrl(workspaceId),
          }),
        );
      }
      listenerApi.dispatch(
        workspaceListActions.toggleWorkspaceDialog({
          isWorkspaceListDialogOpen: false,
        }),
      );

      // fetch workspace
      const workspaceData = await getQueryClient().fetchQuery(
        workspaceQueryOptions.getWorkspacePreset(
          workspaceId,
          common?.auth?.credentials,
        ),
      );

      if (!workspaceData) {
        throw new Error(ERROR_NOT_FOUND);
      }

      listenerApi.dispatch(
        workspaceActions.loadedWorkspace({
          workspaceId,
          workspace: workspaceData,
        }),
      );

      // show all views inside a workspace as loading
      const fullLoadingWorkspace = getLoadingWorkspace(workspaceData);
      listenerApi.dispatch(
        workspaceActions.changePreset({
          workspacePreset: fullLoadingWorkspace,
        }),
      );

      await listenerApi.condition(
        (_, state: WorkspaceModuleStore) =>
          state.workspace?.id === fullLoadingWorkspace.id,
      );

      // fetch all individual view presets in batches and collect all actions
      const workspaceIdsInBatches = getListInChunks(workspaceData.views);
      const actionsToDispatch: WorkspaceActionType[] = [];
      await Promise.all(
        workspaceIdsInBatches.map(async (batchCall) => {
          await Promise.all(
            batchCall.map(async (view) => {
              if (!view.viewPresetId) {
                return;
              } // skip inline-only views

              // eslint-disable-next-line consistent-return
              return fetchWorkspaceViewPreset(
                listenerApi.dispatch,
                view.viewPresetId,
                view.mosaicNodeId,
                common?.auth.credentials,
              );
            }),
          );
        }),
      );

      // Re-apply feature links
      listenerApi.dispatch(
        genericActions.setLinkedMap({
          newLinks: workspaceData.linking ?? {},
        }),
      );

      // Trigger all actions at once
      actionsToDispatch.map(listenerApi.dispatch);
    } catch (error) {
      // If the listener was canceled, we do not need to dispatch any error. will trigger on mount in strictmode.
      if (listenerApi.signal.aborted) {
        console.warn(
          'Fetch workspace listener was canceled, this should not happen too often. Triggers on mount in strict mode.',
        );
        return;
      }
      const errorType =
        (error as Error).message === ERROR_NOT_FOUND
          ? WorkspaceErrorType.NOT_FOUND
          : WorkspaceErrorType.GENERIC;
      listenerApi.dispatch(
        workspaceActions.errorWorkspace({
          workspaceId,
          error: {
            message: getErrorMessage(error as AxiosError),
            type: errorType,
          },
        }),
      );
    }
  },
});

workspaceListener.startListening({
  actionCreator: workspaceActions.saveWorkspacePreset,
  effect: async ({ payload }, listenerApi) => {
    // Cancel any in-progress instances of this listener
    listenerApi.cancelActiveListeners();
    const { workspace } = payload;

    try {
      const credentials = listenerApi.getState().common?.auth?.credentials;
      await getQueryClient().fetchQuery(
        workspaceQueryOptions.saveWorkspacePreset(
          workspace.id!,
          workspace,
          credentials,
        ),
      );
      await getQueryClient().invalidateQueries(
        workspaceQueryOptions.getWorkspacePreset(workspace.id!, credentials),
      );

      listenerApi.dispatch(workspaceActions.loadedWorkspace(undefined!));

      listenerApi.dispatch(
        workspaceListActions.onSuccessWorkspacePresetAction({
          action: WorkspacePresetAction.SAVE,
          workspaceId: workspace.id!,
          title: workspace.title,
          scope: workspace.scope ?? 'user',
          views: workspace.views || [emptyWorkspaceBEData],
          username: workspace.username ?? '',
        }),
      );
    } catch (error) {
      listenerApi.dispatch(
        workspaceActions.errorWorkspace({
          workspaceId: workspace.id!,
          error: {
            message: (error as Error).message,
            type: WorkspaceErrorType.SAVE,
          },
        }),
      );
    }
  },
});

workspaceListener.startListening({
  actionCreator: workspaceActions.changeToEmptyMapWorkspace,
  effect: async (action, listenerApi) => {
    const { newMapPresetText, newWorkspaceText } = action.payload;
    // Cancel any in-progress instances of this listener
    listenerApi.cancelActiveListeners();
    const currentWorkspace = listenerApi.getState().workspace;
    if (currentWorkspace?.id === '' && currentWorkspace?.mosaicNode) {
      return;
    }
    const intialViewMapPreset = mapSelectors.getdefaultMapSettings(
      listenerApi.getState(),
    );
    listenerApi.dispatch(
      workspaceActions.changePreset({
        workspacePreset: createCustomEmptyMapWorkspace(
          newWorkspaceText,
          newMapPresetText,
          intialViewMapPreset,
        ),
      }),
    );
  },
});

const generateInitialProps = (
  componentType: string,
  originalInitialProps: InitialProps,
  copyOriginalcontent: boolean,
): InitialProps => {
  // If we are copying the original content, use that
  if (copyOriginalcontent) {
    return originalInitialProps;
  }
  // Add a new map, copy over the bbox and srs if present
  if (
    componentType === 'Map' &&
    (originalInitialProps as InitialMapProps)?.mapPreset?.proj?.bbox.bottom
  ) {
    const { proj } = (originalInitialProps as InitialMapProps).mapPreset;
    return {
      mapPreset: { proj: { bbox: proj?.bbox, srs: proj?.srs } },
      syncGroupsIds: [],
    };
  }
  // Split button was pressed on a non-map component so we need to use default map settings
  if (componentType === 'Map') {
    return {
      mapPreset: {},
      syncGroupsIds: [],
    };
  }
  // In all other cases, return empty initialProps
  return {};
};

workspaceListener.startListening({
  actionCreator: workspaceActions.setNewWorkspaceView,
  effect: async ({ payload }, listenerApi) => {
    const { mosaicNodeId, componentType, newId, copyOriginalcontent } = payload;

    if (componentType === 'Map') {
      // TODO: this would be nice to move into the openlayers store
      createViewObjectForNewMap(mosaicNodeId, newId);
    }

    // Construct new initialProps from original preset if needed
    const originalInitialProps =
      getViewPresetToSendToBackend(listenerApi.getState(), mosaicNodeId, true)
        .initialProps ?? {};

    const initialProps = generateInitialProps(
      componentType,
      originalInitialProps,
      copyOriginalcontent,
    );

    listenerApi.dispatch(
      workspaceActions.addWorkspaceView({
        componentType,
        id: newId,
        initialProps,
      }),
    );

    if (componentType === 'Map') {
      ((originalInitialProps as InitialMapProps).syncGroupsIds ?? [])
        .filter(
          (groupId) =>
            listenerApi.getState().syncGroups?.groups.byId[groupId]?.type !==
            syncConstants.SYNCGROUPS_TYPE_SETFEATURES,
        )
        .forEach((groupId) =>
          listenerApi.dispatch(
            syncGroupsActions.syncGroupAddTarget({
              groupId,
              targetId: newId,
              linked: true,
            }),
          ),
        );
    }

    // Copy sync group memberships for non-Map panels (e.g. Soundings)
    if (copyOriginalcontent && componentType !== 'Map') {
      syncGroupsSelectors
        .getAllTargetGroupsForSource(listenerApi.getState(), mosaicNodeId)
        .filter(
          (groupId) =>
            listenerApi.getState().syncGroups?.groups.byId[groupId]?.type !==
            syncConstants.SYNCGROUPS_TYPE_SETFEATURES,
        )
        .forEach((groupId) =>
          listenerApi.dispatch(
            syncGroupsActions.syncGroupAddTarget({
              groupId,
              targetId: newId,
              linked: true,
            }),
          ),
        );
    }

    // Copy linked map setting for non-Map panels (e.g. Soundings)
    if (copyOriginalcontent && componentType !== 'Map') {
      const linkedMapIds = genericSelectors.getLinkedTargetIds(
        listenerApi.getState(),
        mosaicNodeId,
      );
      if (linkedMapIds.length > 0) {
        listenerApi.dispatch(
          syncGroupsActions.updateLinkedMap({
            panelId: newId,
            mapIds: linkedMapIds,
          }),
        );
      }
    }

    // Update map to use all dimensions as set in original map
    if (
      componentType === 'Map' &&
      (initialProps as InitialMapProps).mapPreset?.dimensions
    ) {
      listenerApi.dispatch(
        mapActions.mapUpdateAllMapDimensions({
          mapId: newId,
          dimensions: (initialProps as InitialMapProps).mapPreset.dimensions!,
          origin: 'workspace/listener.ts, setNewWorkspaceView',
        }),
      );
    }
  },
});

const waitForFrame = async (): Promise<void> =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

workspaceListener.startListening({
  actionCreator: workspaceActions.updateWorkspaceViews,
  effect: async (_, listenerApi) => {
    // Cancel any in-progress instances of this listener
    listenerApi.cancelActiveListeners();

    await waitForFrame();
    /* This will trigger all maps and other components
    to get the correct size after views are updated */
    window.dispatchEvent(new Event('resize'));
    // Adjust the bbox of the original view after the new map is added. This is a temporal solution until the OpenLayers ticket
    // https://github.com/openlayers/openlayers/issues/16572 is solved.
    await waitForFrame();
    const viewIds = getViewIds(listenerApi.getState());
    viewIds.forEach((viewId) => {
      const bbox = mapSelectors.getBbox(listenerApi.getState(), viewId);
      const srs = mapSelectors.getSrs(listenerApi.getState(), viewId);
      if (bbox?.left !== undefined && srs) {
        listenerApi.dispatch(mapActions.setBbox({ mapId: viewId, bbox, srs }));
      }
    });
  },
});
